

export const CHART_COLORS = [
  "#2b74e4",
  "#94d0b5",
  "#f6c15f",
  "#8d4fd3",
  "#5d97ee",
  "#5abf8b",
  "#f0a84a",
  "#b07ae9",
] as const;

export type OrderTypeId = "dine_in" | "takeaway" | "delivery" | "drive_thru" | "pickup";
export type ChannelId = "mobile_app" | "website" | "kiosk" | "pos" | "call_center" | "aggregator";
export type DateRangeId = "last_7_days" | "last_30_days" | "last_90_days" | "ytd";
export type MenuCategoryId =
  | "grills"
  | "rice_dishes"
  | "sandwiches"
  | "appetizers"
  | "beverages"
  | "desserts";

export const DATE_RANGES: DateRangeId[] = ["last_7_days", "last_30_days", "last_90_days", "ytd"];

export const RANGE_DAYS: Record<DateRangeId, number> = {
  last_7_days: 7,
  last_30_days: 30,
  last_90_days: 90,
  ytd: 180,
};

export const DEMO_TODAY = new Date(Date.UTC(2026, 7, 24));


export const DEMO_MIN_DATE = (() => {
  const sixMonthsBack = new Date(DEMO_TODAY);
  sixMonthsBack.setUTCMonth(sixMonthsBack.getUTCMonth() - 6);
  const yearStart = new Date(Date.UTC(DEMO_TODAY.getUTCFullYear(), 0, 1));
  return sixMonthsBack < yearStart ? sixMonthsBack : yearStart;
})();

export interface Branch {
  id: string;
  name: string;
  nameAr: string;
  city: string;
  cityAr: string;
  lat: number;
  lng: number;
  share: number;
  avgPrepMinutes: number;
  rating: number;
  seats: number;
}

export const BRANCHES: Branch[] = [
  { id: "riy-olaya", name: "Riyadh - Olaya", nameAr: "الرياض - العليا", city: "Riyadh", cityAr: "الرياض", lat: 24.6939, lng: 46.6852, share: 0.185, avgPrepMinutes: 22, rating: 4.6, seats: 120 },
  { id: "riy-narjis", name: "Riyadh - Al Narjis", nameAr: "الرياض - النرجس", city: "Riyadh", cityAr: "الرياض", lat: 24.8277, lng: 46.6291, share: 0.132, avgPrepMinutes: 24, rating: 4.4, seats: 90 },
  { id: "jed-tahlia", name: "Jeddah - Tahlia", nameAr: "جدة - التحلية", city: "Jeddah", cityAr: "جدة", lat: 21.5646, lng: 39.1553, share: 0.158, avgPrepMinutes: 21, rating: 4.7, seats: 110 },
  { id: "jed-corniche", name: "Jeddah - Corniche", nameAr: "جدة - الكورنيش", city: "Jeddah", cityAr: "جدة", lat: 21.5892, lng: 39.0994, share: 0.121, avgPrepMinutes: 23, rating: 4.5, seats: 130 },
  { id: "dam-corniche", name: "Dammam - Corniche", nameAr: "الدمام - الكورنيش", city: "Dammam", cityAr: "الدمام", lat: 26.4344, lng: 50.1033, share: 0.098, avgPrepMinutes: 27, rating: 4.1, seats: 85 },
  { id: "khb-rakah", name: "Khobar - Al Rakah", nameAr: "الخبر - الراكة", city: "Khobar", cityAr: "الخبر", lat: 26.2794, lng: 50.2083, share: 0.089, avgPrepMinutes: 25, rating: 4.3, seats: 80 },
  { id: "mak-aziziyah", name: "Makkah - Al Aziziyah", nameAr: "مكة - العزيزية", city: "Makkah", cityAr: "مكة", lat: 21.4103, lng: 39.8281, share: 0.153, avgPrepMinutes: 26, rating: 4.2, seats: 100 },
  { id: "mad-quba", name: "Madinah - Quba", nameAr: "المدينة - قباء", city: "Madinah", cityAr: "المدينة", lat: 24.4394, lng: 39.6134, share: 0.064, avgPrepMinutes: 24, rating: 4.4, seats: 70 },
];

interface MixItem<T extends string> {
  id: T;
  share: number;
  aovIndex: number;
}

export const ORDER_TYPE_MIX: MixItem<OrderTypeId>[] = [
  { id: "dine_in", share: 0.32, aovIndex: 1.18 },
  { id: "delivery", share: 0.34, aovIndex: 0.94 },
  { id: "takeaway", share: 0.19, aovIndex: 0.86 },
  { id: "drive_thru", share: 0.1, aovIndex: 0.82 },
  { id: "pickup", share: 0.05, aovIndex: 0.9 },
];

export const CHANNEL_MIX: MixItem<ChannelId>[] = [
  { id: "mobile_app", share: 0.34, aovIndex: 1.02 },
  { id: "aggregator", share: 0.26, aovIndex: 0.88 },
  { id: "pos", share: 0.2, aovIndex: 1.12 },
  { id: "website", share: 0.1, aovIndex: 0.97 },
  { id: "kiosk", share: 0.06, aovIndex: 1.05 },
  { id: "call_center", share: 0.04, aovIndex: 0.91 },
];

export const BUSINESS_30D = {
  revenue: 1_862_400,
  orders: 18_450,
  get aov() {
    return this.revenue / this.orders;
  },
  customers: 11_240,
  newCustomers: 3_186,
  repeatRate: 0.417,
  cancelledOrders: 512,
  refundedAmount: 41_820,
  discountAmount: 96_540,
  avgItemsPerOrder: 3.4,
  avgFulfilmentMinutes: 28.6,
};

export interface DailyPoint {
  date: string;
  revenue: number;
  orders: number;
  aov: number;
  customers: number;
  cancelled: number;
}

export interface Daypart {
  id: string;
  share: number;
  hours: number[];
}

export const DAYPARTS: Daypart[] = [
  { id: "breakfast", share: 0.086, hours: [7, 8, 9, 10] },
  { id: "lunch", share: 0.248, hours: [12, 13, 14] },
  { id: "afternoon", share: 0.124, hours: [15, 16, 17] },
  { id: "dinner", share: 0.386, hours: [18, 19, 20, 21] },
  { id: "late_night", share: 0.156, hours: [22, 23, 0] },
];

export const HEATMAP_HOURS = [10, 12, 14, 16, 18, 20, 22, 0] as const;
export const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

/** Sun→Sat weekday demand factor; Thu/Fri/Sat are the Saudi weekend peak. */
export const WEEKDAY_FACTOR = [0.92, 0.86, 0.88, 0.95, 1.16, 1.28, 1.18];

export interface MenuItem {
  id: string;
  name: string;
  nameAr: string;
  category: MenuCategoryId;
  price: number;
  unitShare: number;
  attachRate: number;
}

export const MENU_CATEGORIES: MenuCategoryId[] = [
  "grills",
  "rice_dishes",
  "sandwiches",
  "appetizers",
  "beverages",
  "desserts",
];

export const MENU_ITEMS: MenuItem[] = [
  { id: "mixed-grill", name: "Mixed Grill Platter", nameAr: "مشاوي مشكلة", category: "grills", price: 62, unitShare: 0.084, attachRate: 0.31 },
  { id: "chicken-shawarma", name: "Chicken Shawarma", nameAr: "شاورما دجاج", category: "sandwiches", price: 22, unitShare: 0.121, attachRate: 0.42 },
  { id: "kabsa-chicken", name: "Chicken Kabsa", nameAr: "كبسة دجاج", category: "rice_dishes", price: 38, unitShare: 0.098, attachRate: 0.28 },
  { id: "kabsa-lamb", name: "Lamb Kabsa", nameAr: "كبسة لحم", category: "rice_dishes", price: 55, unitShare: 0.062, attachRate: 0.19 },
  { id: "beef-shawarma", name: "Beef Shawarma", nameAr: "شاورما لحم", category: "sandwiches", price: 24, unitShare: 0.089, attachRate: 0.37 },
  { id: "grilled-kebab", name: "Grilled Kebab Skewers", nameAr: "كباب مشوي", category: "grills", price: 48, unitShare: 0.071, attachRate: 0.26 },
  { id: "hummus", name: "Hummus", nameAr: "حمص", category: "appetizers", price: 14, unitShare: 0.076, attachRate: 0.54 },
  { id: "fattoush", name: "Fattoush Salad", nameAr: "سلطة فتوش", category: "appetizers", price: 16, unitShare: 0.058, attachRate: 0.33 },
  { id: "grilled-chicken", name: "Grilled Chicken Half", nameAr: "نصف دجاجة مشوية", category: "grills", price: 34, unitShare: 0.067, attachRate: 0.24 },
  { id: "falafel-wrap", name: "Falafel Wrap", nameAr: "ساندويش فلافل", category: "sandwiches", price: 15, unitShare: 0.054, attachRate: 0.21 },
  { id: "soft-drink", name: "Soft Drink", nameAr: "مشروب غازي", category: "beverages", price: 7, unitShare: 0.112, attachRate: 0.61 },
  { id: "fresh-juice", name: "Fresh Juice", nameAr: "عصير طازج", category: "beverages", price: 13, unitShare: 0.048, attachRate: 0.29 },
  { id: "kunafa", name: "Kunafa", nameAr: "كنافة", category: "desserts", price: 19, unitShare: 0.032, attachRate: 0.17 },
  { id: "umm-ali", name: "Umm Ali", nameAr: "أم علي", category: "desserts", price: 17, unitShare: 0.026, attachRate: 0.14 },
  { id: "moutabal", name: "Moutabal", nameAr: "متبل", category: "appetizers", price: 15, unitShare: 0.041, attachRate: 0.22 },
  { id: "grilled-fish", name: "Grilled Fish Fillet", nameAr: "سمك مشوي", category: "grills", price: 58, unitShare: 0.021, attachRate: 0.09 },
];

export const CUSTOMER_SEGMENTS = [
  { id: "new", share: 0.284, aovIndex: 0.86, ordersPerCustomer: 1 },
  { id: "returning", share: 0.412, aovIndex: 1.0, ordersPerCustomer: 2.4 },
  { id: "loyal", share: 0.201, aovIndex: 1.24, ordersPerCustomer: 5.1 },
  { id: "at_risk", share: 0.103, aovIndex: 0.91, ordersPerCustomer: 1.6 },
];

export interface RetentionCohort {
  cohort: string;
  size: number;
  retention: number[];
}

export const RETENTION_COHORTS: RetentionCohort[] = [
  { cohort: "2026-03", size: 2140, retention: [1, 0.44, 0.34, 0.28, 0.24, 0.21] },
  { cohort: "2026-04", size: 2260, retention: [1, 0.46, 0.36, 0.29, 0.25] },
  { cohort: "2026-05", size: 2380, retention: [1, 0.47, 0.37, 0.3] },
  { cohort: "2026-06", size: 2510, retention: [1, 0.48, 0.38] },
  { cohort: "2026-07", size: 2640, retention: [1, 0.49] },
  { cohort: "2026-08", size: 2790, retention: [1] },
];

export const CART_FUNNEL = [
  { id: "cart_created", value: 26_480 },
  { id: "cart_with_items", value: 21_780 },
  { id: "checkout_started", value: 16_420 },
  { id: "payment_attempted", value: 13_960 },
  { id: "order_placed", value: 12_640 },
];

export const ABANDON_REASONS = [
  { id: "delivery_fee", share: 0.244 },
  { id: "long_wait", share: 0.198 },
  { id: "payment_failed", share: 0.167 },
  { id: "changed_mind", share: 0.152 },
  { id: "app_issue", share: 0.134 },
  { id: "price_comparison", share: 0.105 },
];

export const RECOVERY_CAMPAIGNS = [
  { id: "push_15min", sent: 6_420, recovered: 968, revenue: 96_320 },
  { id: "sms_1hr", sent: 5_180, recovered: 612, revenue: 58_940 },
  { id: "whatsapp_3hr", sent: 4_760, recovered: 741, revenue: 71_280 },
  { id: "email_24hr", sent: 3_920, recovered: 318, revenue: 29_460 },
  { id: "discount_48hr", sent: 2_640, recovered: 402, revenue: 41_180 },
];

export const ABANDONED_CART = {
  get abandonedCarts() {
    return CART_FUNNEL[0].value - CART_FUNNEL[CART_FUNNEL.length - 1].value;
  },
  get abandonRate() {
    return this.abandonedCarts / CART_FUNNEL[0].value;
  },
  lostRevenue: 612_400,
  recoveredOrders: 3_041,
  recoveredRevenue: 297_180,
  get recoveryRate() {
    return this.recoveredOrders / this.abandonedCarts;
  },
};

export const FULFILMENT_BUCKETS = [
  { id: "under_20", share: 0.212 },
  { id: "20_30", share: 0.404 },
  { id: "30_45", share: 0.276 },
  { id: "over_45", share: 0.108 },
];

/** FNV-1a hash used to seed the deterministic PRNG so demo numbers stay stable per filter key. */
function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 PRNG — deterministic, fast, good enough for demo-data jitter. */
function rng(seed: number): () => number {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function deterministicDelta(seed: string, center: number, spread: number): number {
  const random = rng(hashSeed(seed));
  return center + (random() * 2 - 1) * spread;
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function buildDailySeries(days: number, seedKey: string, endDate: Date = DEMO_TODAY): DailyPoint[] {
  const random = rng(hashSeed(seedKey));
  const baseOrders = BUSINESS_30D.orders / 30;
  const points: DailyPoint[] = [];

  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(endDate);
    date.setUTCDate(date.getUTCDate() - i);
    const weekday = date.getUTCDay();
    const weekdayFactor = WEEKDAY_FACTOR[weekday];
    const trendFactor = 1 + (0.12 * (days - i)) / days;
    const noise = 0.9 + random() * 0.2;

    const orders = Math.max(1, Math.round(baseOrders * weekdayFactor * trendFactor * noise));
    const revenue = Math.round(orders * BUSINESS_30D.aov * (0.95 + random() * 0.1));
    const cancelled = Math.round(orders * 0.028);
    const customers = Math.round(orders * 0.61);
    const aov = orders > 0 ? revenue / orders : 0;

    points.push({ date: toIsoDate(date), revenue, orders, aov, customers, cancelled });
  }

  return points;
}

const HOUR_WEIGHTS: Record<number, number> = {
  20: 1.5,
  18: 1.28,
  12: 1.12,
  14: 1.0,
  22: 0.94,
  0: 0.52,
};

export interface HeatmapCell {
  weekday: number;
  hour: number;
  orders: number;
}

export function buildHeatmap(totalOrders: number): HeatmapCell[] {
  const cells: { weekday: number; hour: number; weight: number }[] = [];
  let weightSum = 0;

  for (let weekday = 0; weekday < 7; weekday += 1) {
    for (const hour of HEATMAP_HOURS) {
      const weight = (HOUR_WEIGHTS[hour] ?? 0.7) * WEEKDAY_FACTOR[weekday];
      weightSum += weight;
      cells.push({ weekday, hour, weight });
    }
  }

  return cells.map(cell => ({
    weekday: cell.weekday,
    hour: cell.hour,
    orders: Math.round((cell.weight / weightSum) * totalOrders),
  }));
}
